import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { adminService, loanService } from '../../services/loanService';
import { formatDate, formatCurrency } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_META = {
  pending:      { label: 'Pending',      color: '#ff9900' },
  under_review: { label: 'Under Review', color: '#5B8EFF' },
  approved:     { label: 'Approved',     color: '#00C896' },
  rejected:     { label: 'Rejected',     color: '#FF4757' },
  escalated:    { label: 'Escalated',    color: '#818CF8' },
  cancelled:    { label: 'Cancelled',    color: '#94a3b8' },
};

const LOAN_STATUS_META = {
  submitted:    { label: 'Submitted',    color: '#5B8EFF' },
  under_review: { label: 'Under Review', color: '#ff9900' },
  credit_check: { label: 'Credit Check', color: '#818CF8' },
  offer_sent:   { label: 'Offer Sent',   color: '#00C896' },
  approved:     { label: 'Approved',     color: '#00C896' },
  rejected:     { label: 'Rejected',     color: '#FF4757' },
  disbursed:    { label: 'Disbursed',    color: '#00C878' },
  active:       { label: 'Active',       color: '#00C878' },
};

const ALL_DOC_TYPES = [
  { value: 'bank_statement',   label: 'Bank Statement' },
  { value: 'id_front',         label: 'ID Card (Front)' },
  { value: 'id_back',          label: 'ID Card (Back)' },
  { value: 'proof_of_address', label: 'Proof of Address' },
  { value: 'pay_slip',         label: 'Pay Slip' },
  { value: 'tax_return',       label: 'Tax Return' },
  { value: 'business_reg',     label: 'Business Registration' },
  { value: 'other',            label: 'Other' },
];

// ── Shared badge ──────────────────────────────────────────────────────────────

function Badge({ value, meta }) {
  const m = meta[value] || { label: value, color: '#94a3b8' };
  return (
    <span style={{
      fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.6rem',
      borderRadius: 99, textTransform: 'uppercase', letterSpacing: '0.05em',
      background: `${m.color}18`, color: m.color, border: `1px solid ${m.color}30`,
    }}>{m.label}</span>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, color, to, urgent, sub }) {
  const navigate = useNavigate();
  return (
    <div
      className="stat-card"
      onClick={() => to && navigate(to)}
      style={{
        cursor: to ? 'pointer' : 'default',
        transition: 'transform .15s, box-shadow .15s',
        position: 'relative', overflow: 'hidden',
        ...(urgent && { borderColor: `${color}40` }),
      }}
      onMouseEnter={e => { if (to) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 8px 32px ${color}20`; } }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
    >
      {urgent && (
        <span style={{
          position: 'absolute', top: 8, right: 8, width: 8, height: 8,
          borderRadius: '50%', background: color, boxShadow: `0 0 8px ${color}`,
          animation: 'pulse 2s infinite',
        }} />
      )}
      <div className="stat-value" style={{ color }}>{value}</div>
      <div className="stat-label">{label}</div>
      {sub && <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', marginTop: '0.2rem' }}>{sub}</div>}
      {to && <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', marginTop: '0.35rem' }}>Click to view →</div>}
    </div>
  );
}

// ── Review Modal ──────────────────────────────────────────────────────────────

function ReviewModal({ appId, onClose, onActionDone }) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: loan, isLoading } = useQuery({
    queryKey: ['admin-loan-detail', appId],
    queryFn: () => adminService.getAdminLoan(appId).then(r => r.data.data),
    enabled: !!appId,
  });

  const [action, setAction]               = useState('under_review');
  const [amount, setAmount]               = useState('');
  const [rate, setRate]                   = useState('');
  const [tenure, setTenure]               = useState('');
  const [notes, setNotes]                 = useState('');
  const [rejectionReason, setRejection]   = useState('');
  const [docSelections, setDocSelections] = useState({});
  const [err, setErr]                     = useState('');
  const [done, setDone]                   = useState(null); // { ref, msg }

  useEffect(() => {
    if (loan) {
      setAmount(loan.approved_amount || loan.requested_amount || '');
      setRate(loan.applied_interest_rate || '18');
      setTenure(loan.requested_tenure_months || '');
    }
  }, [loan?.id]);

  // Debounced schedule preview
  const [previewParams, setPreviewParams] = useState(null);
  useEffect(() => {
    if (action !== 'approve') { setPreviewParams(null); return; }
    const t = setTimeout(() => {
      const a = parseFloat(amount), r = parseFloat(rate), ten = parseInt(tenure);
      if (a > 0 && r > 0 && ten > 0) {
        setPreviewParams({
          amount: a, interest_rate: r, tenure_months: ten,
          payment_count: loan?.payment_count || 1,
          loan_product_id: loan?.loan_product || undefined,
        });
      }
    }, 500);
    return () => clearTimeout(t);
  }, [action, amount, rate, tenure, loan?.payment_count, loan?.loan_product]);

  const { data: schedulePreview } = useQuery({
    queryKey: ['maker-schedule-preview', previewParams],
    queryFn: () => loanService.previewSchedule(previewParams).then(r => r.data.data),
    enabled: !!previewParams,
  });

  const toggleDoc = (val) => setDocSelections(prev => {
    const n = { ...prev };
    if (n[val] !== undefined) delete n[val]; else n[val] = '';
    return n;
  });

  const actionMutation = useMutation({
    mutationFn: (d) => adminService.loanAction(appId, d),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['maker-applications'] });
      qc.invalidateQueries({ queryKey: ['admin-loan-detail', appId] });
      qc.invalidateQueries({ queryKey: ['admin-stats'] });
      if (r.data.approval_ref) {
        setDone({ ref: r.data.approval_ref, msg: r.data.message });
      } else {
        onActionDone?.();
        onClose();
      }
    },
    onError: (e) => setErr(e.response?.data?.message || 'Action failed. Please try again.'),
  });

  const nanoResult    = loan?.credit_check?.nano_result;
  const classicResult = loan?.credit_check?.classic_result;
  const hasCreditCheck = !!(nanoResult || classicResult);
  const needsCheck = ['approve', 'reject'].includes(action) && !hasCreditCheck;

  const handleSubmit = () => {
    setErr('');
    if (action === 'request_docs') {
      const selected = Object.keys(docSelections);
      if (!selected.length) { setErr('Select at least one document type to request.'); return; }
      actionMutation.mutate({ action, notes, documents: selected.map(dt => ({ doc_type: dt, notes: docSelections[dt] })) });
      return;
    }
    const payload = { action, notes };
    if (action === 'approve') {
      payload.approved_amount = parseFloat(amount);
      payload.interest_rate   = parseFloat(rate);
      payload.tenure_months   = parseInt(tenure);
    }
    if (action === 'reject') payload.rejection_reason = rejectionReason;
    actionMutation.mutate(payload);
  };

  // Backdrop + modal wrapper
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--surface-2)', borderRadius: 14,
        border: '1px solid var(--border)', width: '100%', maxWidth: 720,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
      }}>

        {/* Header */}
        <div style={{
          padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          position: 'sticky', top: 0, background: 'var(--surface-2)', zIndex: 1,
        }}>
          <div>
            <span style={{ fontWeight: 700, fontSize: '1rem' }}>Loan Review</span>
            {loan && (
              <span style={{ marginLeft: '0.75rem', fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--text-3)' }}>
                {loan.application_number}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {loan && (
              <button
                className="btn btn-ghost"
                style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}
                onClick={() => navigate(`/admin/applications/${appId}`)}
              >
                Full Details ↗
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-3)', fontSize: '1.25rem', lineHeight: 1,
                padding: '0.15rem 0.4rem', borderRadius: 6,
              }}
            >×</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '1.25rem' }}>
          {isLoading ? (
            <div style={{ padding: '2rem', display: 'flex', justifyContent: 'center' }}><LoadingSpinner /></div>
          ) : !loan ? (
            <p style={{ color: 'var(--text-3)' }}>Could not load application.</p>
          ) : done ? (
            /* Success state */
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✓</div>
              <h3 style={{ fontWeight: 700, color: 'var(--primary)', marginBottom: '0.4rem', fontSize: '1.05rem' }}>
                Submitted for Checker Review
              </h3>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-2)', marginBottom: '1rem' }}>{done.msg}</p>
              <div style={{ display: 'inline-block', background: 'var(--surface-alt)', borderRadius: 8, padding: '0.625rem 1.5rem', marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>Approval Reference</div>
                <div style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary)', fontSize: '0.95rem' }}>{done.ref}</div>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                <button className="btn btn-primary" onClick={() => { onActionDone?.(); onClose(); }}>Close</button>
                <button className="btn btn-ghost" onClick={() => navigate('/admin/approvals')}>View Approval Queue</button>
              </div>
            </div>
          ) : (
            <>
              {/* Loan summary strip */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: '0.625rem', marginBottom: '1.25rem',
                background: 'var(--surface-alt)', borderRadius: 10, padding: '0.875rem 1rem',
                fontSize: '0.8rem',
              }}>
                {[
                  { label: 'Applicant',  value: loan.applicant_name || loan.applicant_email },
                  { label: 'Requested',  value: `₦${parseFloat(loan.requested_amount).toLocaleString()}` },
                  { label: 'Tenure',     value: `${loan.requested_tenure_months} months` },
                  { label: 'Product',    value: loan.product_name || '—' },
                  { label: 'Payments',   value: loan.payment_count || 1 },
                  { label: 'Rate (p.m.)',value: loan.applied_interest_rate ? `${loan.applied_interest_rate}%` : '—' },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
                    <div style={{ fontWeight: 600 }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Already recommended guard */}
              {loan.maker_recommended_at ? (
                <div className="alert alert-info">
                  <span>⚖</span>
                  <span style={{ fontSize: '0.85rem' }}>
                    You have already submitted a recommendation. It is pending checker review.
                  </span>
                </div>
              ) : !['submitted', 'under_review', 'credit_check'].includes(loan.status) ? (
                <div className="alert alert-info">
                  <span>ℹ</span>
                  <span style={{ fontSize: '0.85rem' }}>This application is closed and cannot be actioned.</span>
                </div>
              ) : (
                <>
                  {/* Bureau check warning */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.625rem 0.875rem', marginBottom: '1rem',
                    borderRadius: 8, fontSize: '0.8rem',
                    background: hasCreditCheck ? 'rgba(0,200,150,0.06)' : 'rgba(255,153,0,0.06)',
                    border: `1px solid ${hasCreditCheck ? 'rgba(0,200,150,0.25)' : 'rgba(255,153,0,0.3)'}`,
                  }}>
                    <span style={{ fontSize: '0.9rem' }}>{hasCreditCheck ? '✓' : '⚠'}</span>
                    <span style={{ flex: 1, color: 'var(--text-2)' }}>
                      {hasCreditCheck
                        ? 'Bureau credit check completed.'
                        : 'No bureau credit check yet. Required before approve / reject.'}
                    </span>
                    <button
                      className="btn btn-ghost"
                      style={{ fontSize: '0.72rem', padding: '0.2rem 0.6rem', whiteSpace: 'nowrap' }}
                      onClick={() => navigate(`/admin/applications/${appId}`)}
                    >
                      {hasCreditCheck ? 'View Report' : 'Run Check'} ↗
                    </button>
                  </div>

                  {/* Action select */}
                  <div className="form-group">
                    <label className="form-label">Action</label>
                    <select
                      className="form-input"
                      value={action}
                      onChange={e => { setAction(e.target.value); setErr(''); }}
                    >
                      <option value="under_review">Mark Under Review</option>
                      <option value="request_docs">Request Additional Documents</option>
                      <option value="approve">Recommend Approval — sends to checker</option>
                      <option value="reject">Recommend Rejection — sends to checker</option>
                    </select>
                  </div>

                  {needsCheck && (
                    <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
                      <span>⚠</span>
                      <span style={{ fontSize: '0.82rem' }}>
                        A bureau credit check must be completed before you can {action} this application.{' '}
                        <button
                          onClick={() => navigate(`/admin/applications/${appId}`)}
                          style={{ background: 'none', border: 'none', color: '#FF4757', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.82rem', padding: 0 }}
                        >
                          Open Full Review ↗
                        </button>
                      </span>
                    </div>
                  )}

                  {/* Approve fields */}
                  {action === 'approve' && (
                    <>
                      {/* Sanctioned reference banner */}
                      {(() => {
                        const requested = parseFloat(loan.requested_amount || 0);
                        const current   = parseFloat(amount || 0);
                        const diff      = requested - current;
                        return (
                          <div style={{
                            background: 'var(--surface-alt)', border: '1px solid var(--border)',
                            borderRadius: 8, padding: '0.5rem 0.875rem', marginBottom: '0.875rem',
                            fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
                          }}>
                            <span style={{ color: 'var(--text-3)' }}>Sanctioned (Requested):</span>
                            <strong>₦{requested.toLocaleString()}</strong>
                            {diff > 0.01 && (
                              <span style={{
                                color: '#ff9900', fontSize: '0.72rem', fontWeight: 600,
                                background: 'rgba(255,153,0,0.08)', border: '1px solid rgba(255,153,0,0.2)',
                                padding: '0.12rem 0.45rem', borderRadius: 99,
                              }}>
                                ↓ Reduced by ₦{diff.toLocaleString()}
                              </span>
                            )}
                            {diff < -0.01 && (
                              <span style={{ color: '#FF4757', fontSize: '0.72rem' }}>⚠ Exceeds requested amount</span>
                            )}
                            {Math.abs(diff) <= 0.01 && current > 0 && (
                              <span style={{ color: '#00C896', fontSize: '0.72rem' }}>Full amount approved</span>
                            )}
                          </div>
                        );
                      })()}

                      <div className="grid-2">
                        <div className="form-group">
                          <label className="form-label">Approved Amount (₦)</label>
                          <input
                            className="form-input" type="number"
                            value={amount} onChange={e => setAmount(e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Interest Rate (% p.m.)</label>
                          <input
                            className="form-input" type="number" step="0.1"
                            value={rate} onChange={e => setRate(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Tenure (months)</label>
                        <input
                          className="form-input" type="number"
                          value={tenure} onChange={e => setTenure(e.target.value)}
                        />
                      </div>

                      {/* Live repayment preview */}
                      {schedulePreview && (
                        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: '0.875rem' }}>
                          <div style={{
                            padding: '0.4rem 0.875rem', background: 'var(--surface-alt)',
                            borderBottom: '1px solid var(--border)', fontSize: '0.7rem',
                            fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.05em',
                          }}>
                            Repayment Preview
                          </div>
                          <div style={{
                            padding: '0.75rem 0.875rem',
                            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.625rem',
                          }}>
                            {[
                              { label: 'Per Installment', value: `₦${parseFloat(schedulePreview.installment_amount).toLocaleString()}`, color: 'var(--primary)' },
                              { label: 'Payments',        value: schedulePreview.payment_count },
                              { label: 'Total Interest',  value: `₦${parseFloat(schedulePreview.total_interest).toLocaleString()}` },
                              { label: 'Total Repayment', value: `₦${parseFloat(schedulePreview.total_repayment).toLocaleString()}` },
                              { label: 'Processing Fee',  value: `₦${parseFloat(schedulePreview.processing_fee).toLocaleString()}` },
                              { label: 'Net to Disburse', value: `₦${parseFloat(schedulePreview.net_disbursement).toLocaleString()}`, color: '#00C896' },
                            ].map(({ label, value, color }) => (
                              <div key={label}>
                                <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', marginBottom: 2 }}>{label}</div>
                                <div style={{ fontWeight: 700, fontSize: '0.85rem', color: color || 'var(--text-1)' }}>{value}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Reject field */}
                  {action === 'reject' && (
                    <div className="form-group">
                      <label className="form-label">Rejection Reason <span style={{ color: '#FF4757' }}>*</span></label>
                      <textarea
                        className="form-input" rows={3}
                        value={rejectionReason} onChange={e => setRejection(e.target.value)}
                        placeholder="Explain why this application is being rejected…"
                      />
                    </div>
                  )}

                  {/* Request docs checkboxes */}
                  {action === 'request_docs' && (
                    <div className="form-group">
                      <label className="form-label">Documents to Request <span style={{ color: '#FF4757' }}>*</span></label>
                      <p style={{ fontSize: '0.78rem', color: 'var(--text-3)', marginBottom: '0.625rem' }}>
                        Check each type needed. Add per-document instructions if required.
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                        {ALL_DOC_TYPES.map(({ value, label }) => {
                          const selected = docSelections[value] !== undefined;
                          return (
                            <div key={value} style={{
                              border: `1px solid ${selected ? 'rgba(91,142,255,0.4)' : 'var(--border)'}`,
                              borderRadius: 8, overflow: 'hidden',
                              background: selected ? 'rgba(91,142,255,0.05)' : 'var(--surface-alt)',
                              transition: 'all .15s',
                            }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', padding: '0.5rem 0.875rem', cursor: 'pointer' }}>
                                <input
                                  type="checkbox" checked={selected}
                                  onChange={() => toggleDoc(value)}
                                  style={{ accentColor: '#5B8EFF', width: 14, height: 14, flexShrink: 0 }}
                                />
                                <span style={{ fontSize: '0.82rem', fontWeight: selected ? 600 : 400, color: selected ? '#5B8EFF' : 'var(--text-2)' }}>
                                  {label}
                                </span>
                              </label>
                              {selected && (
                                <div style={{ padding: '0 0.875rem 0.5rem' }}>
                                  <input
                                    className="form-input"
                                    placeholder={`Instructions for ${label} (optional)`}
                                    value={docSelections[value]}
                                    onChange={e => setDocSelections(prev => ({ ...prev, [value]: e.target.value }))}
                                    style={{ fontSize: '0.78rem' }}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {action !== 'request_docs' && (
                    <div className="form-group">
                      <label className="form-label">Internal Notes</label>
                      <textarea
                        className="form-input" rows={2}
                        value={notes} onChange={e => setNotes(e.target.value)}
                        placeholder="Optional notes for this action…"
                      />
                    </div>
                  )}

                  {err && (
                    <div className="alert alert-error" style={{ marginBottom: '0.875rem' }}>
                      <span>⚠</span><span>{err}</span>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button
                      className="btn btn-primary"
                      style={{ minWidth: 140 }}
                      disabled={actionMutation.isPending || needsCheck}
                      onClick={handleSubmit}
                    >
                      {actionMutation.isPending ? 'Processing…' : 'Submit'}
                    </button>
                    <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Payment Schedule expand ───────────────────────────────────────────────────

const SCHEDULE_STATUS_COLOR = {
  pending: '#ff9900',
  paid:    '#00C896',
  overdue: '#FF4757',
  partial: '#818CF8',
};

function ScheduleExpand({ loanId, highlightScheduleId, colSpan }) {
  const { data, isLoading } = useQuery({
    queryKey: ['maker-payment-schedule', loanId],
    queryFn: () => adminService.getLoanSchedule(loanId).then(r => {
      const d = r.data.data;
      return Array.isArray(d) ? d : d.results ?? [];
    }),
    enabled: !!loanId,
  });

  const schedule = data || [];

  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: 0, background: 'var(--surface-alt)', borderBottom: '2px solid var(--border)' }}>
        {isLoading ? (
          <div style={{ padding: '1rem' }}><LoadingSpinner /></div>
        ) : schedule.length === 0 ? (
          <div style={{ padding: '1rem', fontSize: '0.8rem', color: 'var(--text-3)' }}>No repayment schedule found for this loan.</div>
        ) : (
          <div style={{ padding: '0.75rem 1rem' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>
              Repayment Schedule
            </div>
            <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['#', 'Due Date', 'Amount Due', 'Late Fee', 'Paid', 'Balance', 'Status', 'Paid On'].map(h => (
                    <th key={h} style={{ textAlign: h === '#' || h === 'Status' || h === 'Due Date' || h === 'Paid On' ? 'left' : 'right', padding: '0.25rem 0.5rem', color: 'var(--text-3)', fontWeight: 600, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {schedule.map(inst => {
                  const isHighlighted = inst.id === highlightScheduleId;
                  const color = SCHEDULE_STATUS_COLOR[inst.status] || '#94a3b8';
                  return (
                    <tr key={inst.id} style={{
                      borderBottom: '1px solid var(--border)',
                      background: isHighlighted ? 'rgba(129,140,248,0.08)' : 'transparent',
                      borderLeft: isHighlighted ? '3px solid #818CF8' : '3px solid transparent',
                    }}>
                      <td style={{ padding: '0.3rem 0.5rem', fontWeight: isHighlighted ? 700 : 400 }}>{inst.installment_number}</td>
                      <td style={{ padding: '0.3rem 0.5rem', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{formatDate(inst.due_date)}</td>
                      <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(inst.amount_due)}</td>
                      <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', color: inst.late_fee > 0 ? '#FF4757' : 'var(--text-3)' }}>
                        {inst.late_fee > 0 ? formatCurrency(inst.late_fee) : '—'}
                      </td>
                      <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', color: '#00C896' }}>{formatCurrency(inst.amount_paid || 0)}</td>
                      <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', color: (inst.balance || 0) > 0 ? '#FF4757' : 'var(--text-3)' }}>
                        {formatCurrency(inst.balance || 0)}
                      </td>
                      <td style={{ padding: '0.3rem 0.5rem' }}>
                        <span style={{
                          fontSize: '0.62rem', fontWeight: 700, padding: '0.12rem 0.4rem',
                          borderRadius: 99, textTransform: 'uppercase',
                          background: `${color}15`, color, border: `1px solid ${color}30`,
                        }}>
                          {inst.status}
                        </span>
                      </td>
                      <td style={{ padding: '0.3rem 0.5rem', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                        {inst.paid_at ? formatDate(inst.paid_at) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </td>
    </tr>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────

export default function MakerDashboard() {
  const { user, isMaker, loading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState('applications');
  const [paymentErr, setPaymentErr] = useState({});
  const [reviewId, setReviewId] = useState(null);
  const [expandedPaymentId, setExpandedPaymentId] = useState(null);

  useEffect(() => {
    if (!loading && !isMaker) navigate('/admin/dashboard', { replace: true });
  }, [isMaker, loading, navigate]);

  const { isLoading: statsLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => adminService.getDashboardStats().then(r => r.data.data),
  });

  const { data: mySubmissionsData, isLoading: submissionsLoading } = useQuery({
    queryKey: ['maker-my-submissions'],
    queryFn: () => adminService.getApprovals({}).then(r => r.data.data),
  });

  const { data: appsData, isLoading: appsLoading, refetch: refetchApps } = useQuery({
    queryKey: ['maker-applications'],
    queryFn: () => adminService.getAllLoans({ status: 'submitted,under_review,credit_check' }).then(r => r.data.data),
  });

  const { data: paymentsData, isLoading: paymentsLoading } = useQuery({
    queryKey: ['maker-pending-payments'],
    queryFn: () => adminService.getPendingMakerPayments().then(r => r.data.data),
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const reviewPayment = useMutation({
    mutationFn: ({ loanId, paymentId, decision }) =>
      adminService.makerReviewPayment(loanId, paymentId, { decision }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['maker-pending-payments'] });
      qc.invalidateQueries({ queryKey: ['admin-stats'] });
    },
    onError: (e, variables) =>
      setPaymentErr(prev => ({ ...prev, [variables.paymentId]: e.response?.data?.message || 'Failed.' })),
  });

  const allSubmissions = mySubmissionsData?.results ?? mySubmissionsData ?? [];
  const mySubmissions  = allSubmissions.filter(r => r.maker_email === user?.email);
  const pendingChecker = mySubmissions.filter(r => r.status === 'pending' || r.status === 'under_review');
  const approvedByChecker = mySubmissions.filter(r => r.status === 'approved');
  const rejectedByChecker = mySubmissions.filter(r => r.status === 'rejected');

  const allApps = appsData?.results ?? appsData ?? [];
  const actionableApps = allApps.filter(a =>
    ['submitted', 'under_review', 'credit_check'].includes(a.status) && !a.maker_recommended_at
  );

  const pendingPayments = paymentsData?.results ?? paymentsData ?? [];

  return (
    <div>
      {/* Review modal */}
      {reviewId && (
        <ReviewModal
          appId={reviewId}
          onClose={() => setReviewId(null)}
          onActionDone={() => { refetchApps(); qc.invalidateQueries({ queryKey: ['maker-my-submissions'] }); }}
        />
      )}

      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Maker Dashboard</h1>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>
            Logged in as <strong style={{ color: '#00C896' }}>Maker</strong> — you review and recommend actions for checker approval
          </p>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.5rem 1rem',
          background: 'rgba(0,200,150,0.08)', border: '1px solid rgba(0,200,150,0.25)',
          borderRadius: 'var(--radius)', fontSize: '0.8rem',
        }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#00C896', animation: 'pulse 2s infinite', display: 'inline-block' }} />
          <span style={{ color: '#00C896', fontWeight: 700 }}>MAKER MODE</span>
        </div>
      </div>

      {/* Alert: items awaiting checker */}
      {pendingChecker.length > 0 && (
        <div
          onClick={() => navigate('/admin/approvals')}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            padding: '0.75rem 1rem', marginBottom: '1rem',
            background: 'rgba(255,153,0,0.08)', border: '1px solid rgba(255,153,0,0.3)',
            borderRadius: 'var(--radius)', cursor: 'pointer',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,153,0,0.14)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,153,0,0.08)'}
        >
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ff9900', animation: 'pulse 2s infinite', flexShrink: 0 }} />
          <span style={{ fontSize: '0.875rem', flex: 1, color: 'var(--text-1)' }}>
            <strong>{pendingChecker.length}</strong> of your submission{pendingChecker.length > 1 ? 's are' : ' is'} awaiting checker review.
          </span>
          <span style={{ fontSize: '0.75rem', color: '#ff9900', fontWeight: 600 }}>View →</span>
        </div>
      )}

      {/* Stats row */}
      {statsLoading ? <LoadingSpinner /> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
          <StatCard label="Needs My Action" value={actionableApps.length} color="#5B8EFF" to="/admin/applications" urgent={actionableApps.length > 0} sub="Applications to review" />
          <StatCard label="Payments to Review" value={pendingPayments.length} color="#818CF8" urgent={pendingPayments.length > 0} sub="User payments awaiting maker" />
          <StatCard label="Awaiting Checker" value={pendingChecker.length} color="#ff9900" to="/admin/approvals" urgent={pendingChecker.length > 0} sub="My pending submissions" />
          <StatCard label="Approved by Checker" value={approvedByChecker.length} color="#00C896" sub="This session" />
          <StatCard label="Rejected by Checker" value={rejectedByChecker.length} color="#FF4757" sub="My rejected submissions" />
        </div>
      )}

      {/* Quick actions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
        {[
          { icon: '⬡', label: 'Review Applications',  sub: 'Recommend approve / reject',  to: '/admin/applications', color: '#5B8EFF' },
          { icon: '✓', label: 'KYC Review Queue',      sub: 'Review identity documents',   to: '/admin/kyc',          color: '#00C896' },
          { icon: '⚖', label: 'My Submissions',        sub: 'Track checker decisions',     to: '/admin/approvals',    color: '#ff9900' },
        ].map(({ icon, label, sub, to, color }) => (
          <div
            key={to}
            onClick={() => navigate(to)}
            style={{
              padding: '1rem 1.1rem', borderRadius: 'var(--radius)',
              background: 'var(--surface-alt)', border: `1px solid ${color}25`,
              cursor: 'pointer', transition: 'all .15s',
              display: 'flex', alignItems: 'center', gap: '0.875rem',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = `${color}0d`; e.currentTarget.style.borderColor = `${color}50`; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface-alt)'; e.currentTarget.style.borderColor = `${color}25`; }}
          >
            <span style={{ fontSize: '1.3rem', color, flexShrink: 0 }}>{icon}</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{label}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tab content */}
      <div className="card" style={{ padding: 0, marginBottom: '1.5rem' }}>
        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 1rem' }}>
          {[
            { key: 'applications', label: `Applications (${actionableApps.length})` },
            { key: 'submissions',  label: `My Submissions (${mySubmissions.length})` },
            { key: 'payments',     label: `Payments to Review (${pendingPayments.length})`, urgent: pendingPayments.length > 0 },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: '0.75rem 1rem', background: 'none', border: 'none',
                fontSize: '0.82rem', fontWeight: tab === t.key ? 700 : 400,
                color: tab === t.key ? 'var(--primary)' : 'var(--text-3)',
                borderBottom: tab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
                marginBottom: -1, cursor: 'pointer',
              }}
            >
              {t.label}
              {t.urgent && tab !== t.key && (
                <span style={{ marginLeft: 5, width: 6, height: 6, borderRadius: '50%', background: '#818CF8', display: 'inline-block', verticalAlign: 'middle' }} />
              )}
            </button>
          ))}
        </div>

        {/* Applications tab */}
        {tab === 'applications' && (
          appsLoading ? (
            <div style={{ padding: '2rem' }}><LoadingSpinner /></div>
          ) : actionableApps.length === 0 ? (
            <div style={{ padding: '2.5rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
              <p style={{ color: 'var(--text-2)', fontWeight: 600, marginBottom: '0.25rem' }}>All clear</p>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>No applications need your action right now.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table style={{ fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <th>Application #</th>
                    <th>Applicant</th>
                    <th>Amount</th>
                    <th>Product</th>
                    <th>Status</th>
                    <th>Applied</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {actionableApps.map(app => (
                    <tr key={app.id}>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--primary)' }}>
                        {app.application_number}
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{app.applicant_name || app.applicant_email}</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-3)' }}>{app.applicant_email}</div>
                      </td>
                      <td style={{ fontWeight: 700 }}>{formatCurrency(app.approved_amount ?? app.requested_amount)}</td>
                      <td style={{ fontSize: '0.78rem', color: 'var(--text-2)' }}>{app.product_name || '—'}</td>
                      <td><Badge value={app.status} meta={LOAN_STATUS_META} /></td>
                      <td style={{ color: 'var(--text-3)', fontSize: '0.78rem' }}>{formatDate(app.created_at)}</td>
                      <td>
                        <button
                          className="btn btn-primary"
                          style={{ fontSize: '0.72rem', padding: '0.25rem 0.65rem', whiteSpace: 'nowrap' }}
                          onClick={() => navigate(`/admin/applications/${app.id}`)}
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* Payments tab */}
        {tab === 'payments' && (
          paymentsLoading ? (
            <div style={{ padding: '2rem' }}><LoadingSpinner /></div>
          ) : pendingPayments.length === 0 ? (
            <div style={{ padding: '2.5rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
              <p style={{ color: 'var(--text-2)', fontWeight: 600, marginBottom: '0.25rem' }}>No payments pending</p>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>User-submitted payments awaiting your review will appear here.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table style={{ fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <th>Payer</th>
                    <th>Loan</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Submitted</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingPayments.map(p => {
                    const isExpanded = expandedPaymentId === p.id;
                    return (
                      <>
                        <tr key={p.id}>
                          <td>
                            <div style={{ fontWeight: 500 }}>{p.payer_name}</div>
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-3)' }}>{p.payer_email}</div>
                          </td>
                          <td style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--primary)' }}>{p.loan_number}</td>
                          <td style={{ fontWeight: 700 }}>{formatCurrency(p.amount)}</td>
                          <td style={{ fontSize: '0.78rem', textTransform: 'capitalize', color: 'var(--text-2)' }}>
                            {p.method?.replace(/_/g, ' ')}
                          </td>
                          <td style={{ color: 'var(--text-3)', fontSize: '0.78rem' }}>{formatDate(p.paid_at || p.created_at)}</td>
                          <td>
                            {paymentErr[p.id] && (
                              <div style={{ fontSize: '0.7rem', color: '#FF4757', marginBottom: '0.3rem' }}>{paymentErr[p.id]}</div>
                            )}
                            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                              <button
                                className="btn btn-ghost"
                                style={{ fontSize: '0.7rem', padding: '0.25rem 0.65rem', color: '#818CF8', borderColor: '#818CF840' }}
                                onClick={() => setExpandedPaymentId(isExpanded ? null : p.id)}
                              >
                                Schedule {isExpanded ? '▴' : '▾'}
                              </button>
                              <button
                                className="btn btn-primary"
                                style={{ fontSize: '0.7rem', padding: '0.25rem 0.65rem', background: '#1596E0', border: 'none' }}
                                disabled={reviewPayment.isPending}
                                onClick={() => {
                                  setPaymentErr(prev => ({ ...prev, [p.id]: null }));
                                  reviewPayment.mutate({ loanId: p.loan, paymentId: p.id, decision: 'approved' });
                                }}
                              >
                                Approve
                              </button>
                              <button
                                className="btn btn-ghost"
                                style={{ fontSize: '0.7rem', padding: '0.25rem 0.65rem', color: '#FF4757', borderColor: '#FF475740' }}
                                disabled={reviewPayment.isPending}
                                onClick={() => {
                                  setPaymentErr(prev => ({ ...prev, [p.id]: null }));
                                  reviewPayment.mutate({ loanId: p.loan, paymentId: p.id, decision: 'declined' });
                                }}
                              >
                                Decline
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <ScheduleExpand
                            key={`schedule-${p.id}`}
                            loanId={p.loan}
                            highlightScheduleId={p.schedule}
                            colSpan={6}
                          />
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* My Submissions tab */}
        {tab === 'submissions' && (
          submissionsLoading ? (
            <div style={{ padding: '2rem' }}><LoadingSpinner /></div>
          ) : mySubmissions.length === 0 ? (
            <div style={{ padding: '2.5rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>◎</div>
              <p style={{ color: 'var(--text-2)', fontWeight: 600, marginBottom: '0.25rem' }}>No submissions yet</p>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>Actions you recommend will appear here for checker review.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table style={{ fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Title</th>
                    <th>Module</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    <th>Checker Comment</th>
                  </tr>
                </thead>
                <tbody>
                  {mySubmissions.map(req => (
                    <tr key={req.id} style={{
                      background: req.status === 'rejected' ? 'rgba(255,71,87,0.03)'
                        : req.status === 'approved' ? 'rgba(0,200,150,0.02)' : 'transparent',
                    }}>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{req.reference_number}</td>
                      <td style={{ maxWidth: 200 }}>
                        <div style={{ fontWeight: 500 }}>{req.title}</div>
                        {req.description && (
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', marginTop: 2 }}>
                            {req.description.slice(0, 60)}{req.description.length > 60 ? '…' : ''}
                          </div>
                        )}
                      </td>
                      <td style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>{req.module}</td>
                      <td><Badge value={req.status} meta={STATUS_META} /></td>
                      <td style={{ color: 'var(--text-3)', fontSize: '0.78rem' }}>{formatDate(req.created_at)}</td>
                      <td style={{ fontSize: '0.78rem', color: req.status === 'rejected' ? '#FF4757' : 'var(--text-3)', maxWidth: 200 }}>
                        {req.checker_comment || req.decision_comment || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* Role reminder */}
      <div style={{
        padding: '0.875rem 1rem',
        background: 'rgba(0,200,150,0.06)', border: '1px solid rgba(0,200,150,0.2)',
        borderRadius: 'var(--radius)', fontSize: '0.8rem', color: 'var(--text-2)',
      }}>
        <strong style={{ color: '#00C896' }}>Maker Role</strong> — You review applications and recommend actions.
        Every recommendation creates a checker request that a separate admin must approve before taking effect.
        This four-eyes principle protects against unauthorised decisions.
      </div>
    </div>
  );
}
